/**
 * Convenção de chave para **filtros de tela** persistidos pelos front-ends da Cora.
 *
 * A chave canônica é `@cross/cached-filters` — é a que já está em produção no
 * `access-frt` e no `mfe-manager-frt` (`src/context/core/filter.tsx`). Um MFE que
 * precise separar filtros por tela deve usar `buildFilterStorageKey('<escopo>')`,
 * que gera `@cross/cached-filters:<escopo>`.
 *
 * Quem consome:
 *  - a RootApp, na assistência "Limpeza de histórico de filtros", para saber o
 *    que pode apagar do `localStorage`/`sessionStorage`;
 *  - os MFEs, via `@cora/mfe-client` (`api.filters`), para gravar no lugar certo
 *    e reagir ao evento `assistance.filtersCleared`.
 *
 * > ☢ Alterar o prefixo obriga a atualizar todos os MFEs que já persistem filtro.
 */

/** Prefixo canônico das chaves de filtro. */
const CORA_FILTER_STORAGE_PREFIX = '@cross/cached-filters';

/**
 * Rede de segurança para MFEs que ainda não seguem a convenção: qualquer chave
 * que se identifique como filtro também é limpa. Deve sumir quando todos os
 * front-ends migrarem para `buildFilterStorageKey`.
 */
const LEGACY_FILTER_KEY_PATTERNS = [/filt(er|ro)s?/i];

/**
 * Monta a chave de storage de um filtro.
 *
 * @param scope identificador opcional da tela/contexto (ex.: `'usuarios'`)
 */
const buildFilterStorageKey = (scope?: string): string => {
  const cleanScope = scope?.trim();
  return cleanScope
    ? `${CORA_FILTER_STORAGE_PREFIX}:${cleanScope}`
    : CORA_FILTER_STORAGE_PREFIX;
};

/**
 * A chave guarda histórico de filtro?
 */
const isFilterStorageKey = (key: string): boolean => {
  if (!key) {
    return false;
  }

  if (
    key === CORA_FILTER_STORAGE_PREFIX ||
    key.startsWith(`${CORA_FILTER_STORAGE_PREFIX}:`)
  ) {
    return true;
  }

  return LEGACY_FILTER_KEY_PATTERNS.some((pattern) => pattern.test(key));
};

/**
 * Lista as chaves de filtro presentes em um storage.
 */
const listFilterStorageKeys = (storage: Storage): string[] =>
  Object.keys(storage).filter((key) => isFilterStorageKey(key));

export {
  CORA_FILTER_STORAGE_PREFIX,
  LEGACY_FILTER_KEY_PATTERNS,
  buildFilterStorageKey,
  isFilterStorageKey,
  listFilterStorageKeys,
};
